import * as React from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  type SelectChangeEvent,
  Snackbar,
  TextField,
  InputLabel
} from '@mui/material'
import { CountryMultipleSelector, FormDialog, type Module, SimpleDialogQuery, MissingValueSelectComponent, type MissingValueFilter } from 'components'
import { type PositionType, type Country } from 'services'
import { http } from 'interfaces'
import { ProjectModule, useSessionContext } from 'session'
import SaveIcon from '@mui/icons-material/Save'
import { ButtonWithIcon, PositionTypeSelector } from 'components'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

export interface PersonTableFiltersProps {
  filters: PersonFilters
  updateFilters: (filters: any) => void
  doFilter: () => Promise<void>
  cleanFilters: () => void
}

export interface PersonFilters {
  id: string
  uniqueId: string
  first_name: string
  last_name: string
  countries: Country[]
  page: number
  order_field: string
  order_direction: 'ASC' | 'DESC'
  missing_value: MissingValueFilter | undefined
  positionTypes: PositionType[]
  email?: string | null
}

export const PeopleFilters: React.FC<PersonTableFiltersProps> = ({ filters, updateFilters, doFilter, cleanFilters }) => {
  const [isChecked, setIsChecked] = React.useState(false)
  const [queryName, setqueryName] = React.useState('')
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = React.useState(false)
  const { sessionData } = useSessionContext()

  const module: Module = {
    id: ProjectModule.getId(sessionData.module_name) ?? 0,
    name: sessionData.module_name
  }
  const missingValueFilters: Record<string, MissingValueFilter> = {
    Email: { label: 'Email', query: 'no_email', value: 1 },
    Phone: { label: 'Phone', query: 'no_phone', value: true },
    Address: { label: 'Address', query: 'no_address', value: true }
  }

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }
    setShowSuccessAlert(false)
    setShowUnsuccessAlert(false)
  }

  const handleInputChange = (
    event: SelectChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof PersonFilters
  ): void => {
    const value = event.target.value
    const newFilter = { [filterKey]: value }
    updateFilters(newFilter)
  }
  const handleCountryChange = (countries: Country[]): void => {
    const newFilter = { countries }
    updateFilters(newFilter)
  }

  const handleFilterClick = (): void => {
    if (filters.page === 1) {
      doFilter().catch((reason) => { console.log(reason) })
    } else {
      updateFilters({ page: 1 })
    }
  }

  const handleFormCheckboxChange = (isChecked: boolean, queryName: string): void => {
    setIsChecked(isChecked)
    setqueryName(queryName)
  }

  const handleOncloseChange = (queryName: string): void => {
    if (queryName === '') return
    filters = JSON.parse(queryName)
    const parsedQuery = JSON.parse(queryName)
    const newFilter: PersonFilters = {
      first_name: parsedQuery.first_name ?? '',
      last_name: parsedQuery.last_name ?? '',
      id: parsedQuery.id ?? '',
      countries: parsedQuery.countries ?? [],
      page: parsedQuery.page ?? 0,
      order_field: parsedQuery.order_field ?? '',
      order_direction: parsedQuery.order_direction ?? 'ASC',
      missing_value: undefined,
      uniqueId: parsedQuery.uniqueId ?? '',
      email: parsedQuery.email ?? '',
      positionTypes: parsedQuery.positionTypeIds ?? []
    }

    updateFilters(newFilter)
    updateFilters(filters)
  }

  const handleTypePositionSelection = (value: PositionType[]): void => {
    const newFilter = { positionTypes: value }
    updateFilters(newFilter)
  }

  const handlePostClick = function (): any {
    const querySavedRequest = {
      name: queryName,
      is_private: isChecked,
      query: JSON.stringify(filters),
      module: {
        id: ProjectModule.getId(sessionData.module_name)
      }
    }
    http.post('/api/v1/queries-saved/', querySavedRequest, { withCredentials: true })
      .then((response) => {
        if (response.status !== 201) {
          setShowSuccessAlert(false)
          setShowUnsuccessAlert(true)
          return
        }
        setShowSuccessAlert(true)
        setShowUnsuccessAlert(false)
      })
      .catch((error) => {
        setShowSuccessAlert(false)
        setShowUnsuccessAlert(true)
        console.error('Error creating saved Query:', error)
      })
  }

  const iconSaveButtonProps = {
    color: 'primary',
    size: 'large',
    children: <SaveIcon />
  }

  const handlerClean = (): void => {
    cleanFilters()
  }

  const handleMissingValueChange = (
    missingValueFilter: MissingValueFilter | undefined
  ): void => {
    const newFilter = { missing_value: missingValueFilter }
    updateFilters(newFilter)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mt: 3 }}>
      <Card>
        {showSuccessAlert && (
          <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
              This query {queryName} has been successfully saved!
            </Alert>
          </Snackbar>
        )}
        {showUnsuccessAlert && (
          <Snackbar open={showUnsuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
              Failed to save the query!
            </Alert>
          </Snackbar>
        )}
      </Card>
      <Card sx={{ flex: 1, p: 1.5, borderRadius: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <FormControl fullWidth={true} variant="outlined">
                <TextField
                  label="ID"
                  type="number"
                  value={filters.id}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'id') }}
                  variant="outlined" />
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <FormControl fullWidth={true} variant="outlined">
                <TextField
                  label="Unique ID"
                  value={filters.uniqueId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'uniqueId') }}
                  variant="outlined" />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth={true} variant="outlined">
                <TextField
                  label="First Name"
                  value={filters.first_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'first_name') }}
                  variant="outlined" />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth={true} variant="outlined">
                <TextField
                  label="Last Name"
                  value={filters.last_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'last_name') }}
                  variant="outlined" />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl variant="outlined" fullWidth={true}>
                <CountryMultipleSelector handleCountryChange={handleCountryChange} value={filters.countries} />
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <FormControl variant="outlined" fullWidth={true}>
                <InputLabel>Missing Value</InputLabel>
                <MissingValueSelectComponent missingValueFilters={missingValueFilters} missingValue={filters.missing_value} handleChange={handleMissingValueChange} />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <PositionTypeSelector handlePositionTypeChange={handleTypePositionSelection} selectedTypes={filters.positionTypes} />
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth={true} variant="outlined">
                <TextField
                  label="Email"
                  value={filters.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e, 'email') }}
                  variant="outlined"
                />
              </FormControl>
            </Grid>
            <Grid item xs={9} />
              <Grid item xs={1}>
                <ButtonWithIcon fullWidth={true} variant="contained" size='large' nameButton='Search' icon={<FilterAltIcon />} onClick={handleFilterClick}></ButtonWithIcon>
              </Grid>
              <Grid item xs={1}>
                <ButtonWithIcon fullWidth={true} variant="contained" size='large' nameButton='Clean' icon={<CleaningServicesIcon />} onClick={handlerClean}></ButtonWithIcon>
              </Grid>
              <Grid item xs={1}>
                <Grid container justifyContent="center" spacing={1}>
                  <Grid item xs={5}>
                    <FormDialog isShowCheckBox={true} onCheckboxChange={handleFormCheckboxChange} onButtonClick={handlePostClick} title='Saving Search (Filters)' contentText='What name would you like to save the search as?' nameLabelField='Search Name' tooltip='Save This Search' iconButtonProps={iconSaveButtonProps} />
                  </Grid>
                  <Grid xs={2}/>
                  <Grid item xs={5}>
                    <SimpleDialogQuery onClose={handleOncloseChange} module={module}></SimpleDialogQuery>
                  </Grid>
                  </Grid>
              </Grid>
            </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}
